import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, MapPin, Briefcase, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/jobs`);
        if (!cancelled) {
          setJobs(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasJobs = !loading && !error && jobs.length > 0;

  return (
    <div data-testid="careers-page">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-4 mm-fade-up">
          Careers
        </div>

        {loading && (
          <div
            data-testid="careers-loading"
            className="mt-8 flex items-center gap-3 text-slate-500 text-sm"
          >
            <Loader2 size={16} className="animate-spin" />
            Loading open roles…
          </div>
        )}

        {!loading && (
          <>
            {hasJobs ? (
              <CareersWithJobs jobs={jobs} />
            ) : (
              <CareersEmpty />
            )}
          </>
        )}
      </section>
    </div>
  );
}

function CareersEmpty() {
  return (
    <>
      <h1
        data-testid="careers-heading"
        className="mm-fade-up mm-delay-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 max-w-3xl leading-[1.05]"
      >
        No open positions <span className="font-medium">right now.</span>
      </h1>
      <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
        We're always interested in meeting exceptional engineers, architects, and specialists. If you'd like to be considered for future opportunities, send your CV and we'll be in touch when a role matches your strengths.
      </p>

      <div className="mm-fade-up mm-delay-3 mt-10 border border-slate-200 rounded-xl p-8 lg:p-10 bg-slate-50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8 justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-2">
              Send your CV
            </div>
            <a
              href="mailto:careers@t3partner.in?subject=Future opportunity — CV submission"
              data-testid="careers-email-link"
              className="font-heading text-2xl sm:text-3xl font-medium text-slate-900 hover:text-orange-600 transition-colors"
            >
              careers@t3partner.in
            </a>
          </div>
          <Button
            asChild
            data-testid="careers-email-cta"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 px-7 text-sm font-semibold tracking-wide"
          >
            <a href="mailto:careers@t3partner.in?subject=Future opportunity — CV submission">
              <Mail className="mr-2" size={16} /> Email us your CV
            </a>
          </Button>
        </div>
      </div>
    </>
  );
}

function CareersWithJobs({ jobs }) {
  return (
    <>
      <h1
        data-testid="careers-heading"
        className="mm-fade-up mm-delay-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 max-w-3xl leading-[1.05]"
      >
        Open <span className="font-medium">positions.</span>
      </h1>
      <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
        Join a team that pairs elite talent with ambitious product engineering, digital transformation, and managed services engagements.
      </p>

      <ul
        data-testid="careers-jobs-list"
        className="mm-fade-up mm-delay-3 mt-12 border border-slate-200 rounded-xl overflow-hidden bg-white divide-y divide-slate-200"
      >
        {jobs.map((job) => (
          <li key={job.slug}>
            <Link
              to={`/careers/${job.slug}`}
              data-testid={`careers-job-item-${job.slug}`}
              className="group flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-6 lg:p-8 hover:bg-slate-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg lg:text-xl font-medium text-slate-900 group-hover:text-orange-600 transition-colors">
                  {job.title}
                </h3>
                {job.excerpt && (
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2 max-w-2xl">
                    {job.excerpt}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-500">
                  {job.department && (
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase size={13} /> {job.department}
                    </span>
                  )}
                  {job.location && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={13} /> {job.location}
                    </span>
                  )}
                  {job.type && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={13} /> {job.type}
                    </span>
                  )}
                  {job.experience && (
                    <span className="text-slate-500">{job.experience}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 md:flex-col md:items-end md:gap-1 shrink-0">
                {job.posted && (
                  <span className="text-xs uppercase tracking-wider text-slate-500">
                    Posted {job.posted}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 group-hover:text-blue-700">
                  View role <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-12 border border-slate-200 rounded-xl p-8 bg-slate-50 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-1">
            Don't see the right role?
          </div>
          <p className="text-sm text-slate-600 max-w-md">
            Send your CV to <span className="font-medium text-slate-900">careers@t3partner.in</span> and we'll reach out when a role matches your strengths.
          </p>
        </div>
        <Button
          asChild
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-11 px-6"
        >
          <a href="mailto:careers@t3partner.in?subject=Future opportunity — CV submission">
            <Mail className="mr-2" size={16} /> Email us your CV
          </a>
        </Button>
      </div>
    </>
  );
}
